import { useState, useEffect } from 'react';
import { fetchAdminBookings, updateBookingStatus } from '../common/api';

const STATUS_OPTIONS = ['pending', 'deposit_paid', 'pending_balance', 'confirmed', 'cancelled'];

const STATUS_LABEL = {
  pending: { text: 'Pending', cls: 'bg-yellow-100 text-yellow-700' },
  deposit_paid: { text: 'Deposit Paid', cls: 'bg-blue-100 text-blue-700' },
  pending_balance: { text: 'Balance Due', cls: 'bg-orange-100 text-orange-600' },
  confirmed: { text: 'Confirmed', cls: 'bg-green-100 text-green-700' },
  cancelled: { text: 'Cancelled', cls: 'bg-red-100 text-red-600' },
};

function formatKRW(n) {
  return `₩${Number(n).toLocaleString('ko-KR')}`;
}

function formatDate(d) {
  return new Date(d).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

export default function Reservations() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  useEffect(() => { load(); }, [filterStatus, search]);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      if (search) params.search = search;
      const data = await fetchAdminBookings(params);
      setBookings(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function handleSearchSubmit(e) {
    e.preventDefault();
    setSearch(searchInput);
  }

  async function handleStatusChange(id, status) {
    try {
      await updateBookingStatus(id, status);
      setBookings((prev) =>
        prev.map((b) => b.id === id ? { ...b, status } : b)
      );
    } catch (err) {
      alert(err.message);
    }
  }

  return (
    <div className="p-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Reservations</h1>
        <p className="text-gray-500 text-sm mt-1">{bookings.length} bookings</p>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <form onSubmit={handleSearchSubmit} className="flex gap-2">
          <input
            type="text"
            placeholder="Search by email, tour, booking ID..."
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E8876A] w-72"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <button
            type="submit"
            className="px-4 py-2 bg-gray-800 text-white text-sm rounded-lg hover:bg-gray-700 transition-colors"
          >
            Search
          </button>
          {search && (
            <button
              type="button"
              onClick={() => { setSearch(''); setSearchInput(''); }}
              className="px-3 py-2 border border-gray-300 text-gray-500 text-sm rounded-lg hover:bg-gray-50"
            >
              Clear
            </button>
          )}
        </form>

        <div className="flex gap-2">
          {['all', ...STATUS_OPTIONS].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors capitalize ${filterStatus === s
                ? 'bg-[#E8876A] text-white'
                : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-50'
                }`}
            >
              {s === 'all' ? 'All' : STATUS_LABEL[s].text}
            </button>
          ))}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
          ⚠️ {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-gray-400 text-sm">Loading...</div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">#</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Email</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Tour</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Date</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Person(s)</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Total</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Booked At</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {bookings.map((b) => {
                const status = STATUS_LABEL[b.status] ?? STATUS_LABEL.pending;
                return (
                  <tr key={b.id} className="hover:bg-gray-50">
                    <td className="px-5 py-4 text-sm text-gray-400">#{b.id}</td>
                    <td className="px-5 py-4 text-sm text-gray-600">{b.email}</td>
                    <td className="px-5 py-4 text-sm font-medium text-gray-800">{b.tour_title}</td>
                    <td className="px-5 py-4 text-sm text-gray-600">{formatDate(b.date)}</td>
                    <td className="px-5 py-4 text-sm text-gray-600">{b.persons}</td>
                    <td className="px-5 py-4 text-sm font-medium text-gray-800">{formatKRW(b.total)}</td>
                    <td className="px-5 py-4 text-sm text-gray-400">{formatDate(b.created_at)}</td>
                    <td className="px-5 py-4">
                      <select
                        value={b.status}
                        onChange={(e) => handleStatusChange(b.id, e.target.value)}
                        className={`px-2.5 py-1 rounded-full text-xs font-medium border-0 cursor-pointer focus:outline-none ${status.cls}`}
                      >
                        {STATUS_OPTIONS.map((s) => (
                          <option key={s} value={s}>{STATUS_LABEL[s].text}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {!loading && bookings.length === 0 && (
          <div className="py-12 text-center text-gray-400 text-sm">No bookings found.</div>
        )}
      </div>
    </div>
  );
}